import func from './func';
import identity from './identity';
import network from './network';

export default class Request {
    /**
     * @param {{}} config Findologic config
     * @param {string} config.shopUrl Domain of the shop.
     * @param {string} config.shopkey Service ID.
     * @param {boolean} config.useAutocompleteProxy Whether to send suggest requests via a proxy provided by the shop.
     * @param {string} config.autocompleteProxy Path to the proxy provided by the shop for suggest requests.
     * @param {{}} config.directIntegration
     * @param {string} config.directIntegration.frontend Search API base URL.
     * @param {{}} params
     * @param {number} params.timeout Request timeout in milliseconds; defaults to 3000 if not specified.
     * @param {function} params.onSuccess Called when receiving a successful response before timing out.
     * @param {function} params.onError Called when receiving an error response before timing out.
     * @param {function} params.onTimeout Called when when no response is received before timing out.
     * @param {function} params.onComplete Always called when the request is done, regardless of response or timeout.
     */
    constructor(config, params) {
        this.config = config;
        this.params = params;
        this.presetParams = {};

        this.presetParams[Request.PARAM_SHOPURL] = config.shopUrl;
        this.presetParams[Request.PARAM_SHOPKEY] = config.shopkey;
        this.presetParams[Request.PARAM_REQUEST_ID] = identity.createUUID();

        // Apply default params.
        this.params.timeout = this.params.timeout || 3000;
        ['onSuccess', 'onError', 'onTimeout', 'onComplete'].forEach((callbackName) => {
            this.params[callbackName] = func.undefinedToNoOp(this.params[callbackName]);
        });
    }

    buildBaseUrl(operation) {
        if (this.config.useAutocompleteProxy && operation === Request.TYPE_SUGGEST) {
            return this.config.autocompleteProxy;
        } else {
            return this.config.directIntegration.frontend + Request.ENDPOINTS[operation];
        }
    }

    send(operation, queryParams) {
        let request;

        const timeoutHandle = setTimeout(() => {
            request.abort();

            this.params.onTimeout();
            this.params.onComplete();
        }, this.params.timeout);

        if (typeof queryParams.usergrouphash !== 'undefined') {
            this.presetParams.usergrouphash = queryParams.usergrouphash;
        } else {
            this.presetParams.usergrouphash = identity.getUsergrouphash();
        }

        // Ignore empty usergroups.
        if (this.presetParams.usergrouphash.trim() === '') {
            delete queryParams.usergrouphash;
            delete this.presetParams.usergrouphash;
        }

        const url = this.buildBaseUrl(operation) + '?' + new URLSearchParams(Object.assign({}, queryParams, this.presetParams)).toString();

        request = network.get(
            url,
            {
                'Content-Type': 'application/json',
            },
            (status, text) => {
                this.params.onSuccess(text);
            },
            (status, text) => {
                // Ignore errors caused by aborted requests.
                if (status !== 'abort') {
                    this.params.onError(text);
                }
            },
            () => {
                clearTimeout(timeoutHandle);
                this.params.onComplete();
            }
        );

        // Return request object, so the caller has the option to abort the request.
        return request;
    }
}

Request.PARAM_SHOPURL = 'shopurl';
Request.PARAM_SHOPKEY = 'shopkey';
Request.PARAM_USER_ID = 'userId';
Request.PARAM_REQUEST_ID = 'requestId';

Request.TYPE_SEARCH = 'search';
Request.TYPE_NAVIGATION = 'navigation';
Request.TYPE_SUGGEST = 'suggest';

Request.ENDPOINTS = {};
Request.ENDPOINTS[Request.TYPE_SEARCH] = 'index.php';
Request.ENDPOINTS[Request.TYPE_NAVIGATION] = 'selector.php';
Request.ENDPOINTS[Request.TYPE_SUGGEST] = 'autocomplete.php';
